import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  FileText, Plus, Loader2, Calendar, DollarSign, AlertTriangle,
  CheckCircle, Clock, CreditCard, Search, ChevronDown, ChevronRight, Trash2,
  Mail, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupplierInvoices = () => {
  const { getAuthHeader } = useAuth();
  const { selectedStore, storeName } = useStore();
  const [invoices, setInvoices] = useState([]);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState('suppliers');  // 'suppliers' | 'customers'
  const [custSearch, setCustSearch] = useState('');
  const [custFilter, setCustFilter] = useState('unpaid');  // unpaid / all / paid
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  // Month-summary picker — defaults to current month (YYYY-MM)
  const [summaryMonth, setSummaryMonth] = useState(() => new Date().toISOString().slice(0, 7));

  // Payment dialog state
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payNote, setPayNote] = useState('');
  const [paySaving, setPaySaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  
  const [invoiceForm, setInvoiceForm] = useState({
    supplier_id: '',
    supplier_name: '',
    store: selectedStore || 'orewa',
    received_date: new Date().toISOString().split('T')[0],
    amount: 0,
    due_date: '',
    status: 'unpaid'
  });

  const fetchData = async () => {
    try {
      const storeParam = selectedStore ? `&store=${selectedStore}` : '';
      const supplierStoreParam = selectedStore ? `?store=${selectedStore}` : '';
      const [invoicesRes, suppliersRes, custRes] = await Promise.all([
        axios.get(`${API_URL}/invoices${supplierStoreParam}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/customer-invoices?limit=500${storeParam}`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      setInvoices(invoicesRes.data);
      setSuppliers(suppliersRes.data);
      setCustomerInvoices(custRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore]);

  const resetForm = () => {
    setInvoiceForm({
      supplier_id: '',
      supplier_name: '',
      store: selectedStore || 'orewa',
      received_date: new Date().toISOString().split('T')[0],
      amount: 0,
      invoice_number: '',
      due_date: '',
      status: 'unpaid'
    });
  };

  const deleteInvoice = async (invoice) => {
    if (!window.confirm(`Delete invoice from ${invoice.supplier_name} for $${(invoice.amount || 0).toFixed(2)}? This cannot be undone.`)) return;
    let reverse = false;
    if (invoice.po_id) {
      reverse = window.confirm(
        'This invoice came from a received order.\n\nAlso REMOVE THE STOCK that receive added? (Use this to undo a double-up.)\n\nOK = delete invoice AND remove the stock\nCancel = delete the invoice only'
      );
    }
    try {
      const res = await axios.delete(`${API_URL}/invoices/${invoice.id}`, { params: { reverse_stock: reverse }, headers: getAuthHeader() });
      const n = res.data?.stock_reversed_items || 0;
      toast.success(n > 0 ? `Invoice deleted — stock removed for ${n} product${n === 1 ? '' : 's'}` : 'Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setInvoiceForm({
      ...invoiceForm,
      supplier_id: supplierId,
      supplier_name: supplier?.name || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!invoiceForm.supplier_name) {
      toast.error('Please select a supplier');
      return;
    }
    if (!invoiceForm.amount || invoiceForm.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        ...invoiceForm,
        amount: parseFloat(invoiceForm.amount) || 0
      };
      await axios.post(`${API_URL}/invoices`, payload, { headers: getAuthHeader() });
      toast.success('Invoice added');
      setIsAddDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add invoice');
    } finally {
      setFormLoading(false);
    }
  };

  const togglePaidStatus = async (invoice) => {
    try {
      const total = invoice.amount || 0;
      const isPaid = (invoice.paid_amount || 0) >= total - 0.005;
      // Toggle: if currently paid, mark unpaid (paid_amount=0); else mark fully paid (paid_amount=total)
      const newPaid = isPaid ? 0 : total;
      await axios.put(`${API_URL}/invoices/${invoice.id}`, { paid_amount: newPaid }, { headers: getAuthHeader() });
      toast.success(isPaid ? 'Marked unpaid' : 'Marked paid in full');
      fetchData();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const openPayDialog = (invoice) => {
    const balance = (invoice.amount || 0) - (invoice.paid_amount || 0);
    setPayInvoice(invoice);
    setPayAmount(balance > 0 ? balance.toFixed(2) : '');
    setPayNote('');
    setPayDialogOpen(true);
  };

  const handleRecordPayment = async (e) => {
    e?.preventDefault?.();
    if (!payInvoice) return;
    const amt = parseFloat(payAmount);
    if (isNaN(amt) || amt <= 0) {
      toast.error('Enter a valid amount greater than zero');
      return;
    }
    setPaySaving(true);
    try {
      await axios.post(`${API_URL}/invoices/${payInvoice.id}/payments`, { amount: amt, note: payNote }, { headers: getAuthHeader() });
      toast.success(`Recorded $${amt.toFixed(2)} payment`);
      setPayDialogOpen(false);
      setPayInvoice(null);
      setPayAmount('');
      setPayNote('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setPaySaving(false);
    }
  };

  const deletePayment = async (invoiceId, paymentId) => {
    if (!window.confirm('Remove this payment? Totals will be recalculated.')) return;
    try {
      await axios.delete(`${API_URL}/invoices/${invoiceId}/payments/${paymentId}`, { headers: getAuthHeader() });
      toast.success('Payment removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove payment');
    }
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Filter invoices
  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = inv.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || inv.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Calculate totals
  const overdueInvoices = invoices.filter(i => ((i.paid_amount || 0) < (i.amount || 0) - 0.005) && i.due_date && new Date(i.due_date) < new Date());
  const overdueAmount = overdueInvoices.reduce((sum, i) => sum + Math.max(0, (i.amount || 0) - (i.paid_amount || 0)), 0);
  // Unpaid total excludes overdue (so the two cards don't double-count)
  const overdueIds = new Set(overdueInvoices.map(i => i.id));
  const totalUnpaid = invoices
    .filter(i => !overdueIds.has(i.id))
    .reduce((sum, i) => sum + Math.max(0, (i.amount || 0) - (i.paid_amount || 0)), 0);
  const totalPaid = invoices.reduce((sum, i) => sum + (i.paid_amount || 0), 0);
  const overdueCount = overdueInvoices.length;

  // Monthly summary based on the picked month (`YYYY-MM`).
  // PAID = sum of payments dated in that month.
  // OVERDUE = invoices with due_date in that month that are still unpaid as of today.
  const monthMatches = (dateStr, ym) => (dateStr || '').slice(0, 7) === ym;
  const monthLabel = (() => {
    if (!summaryMonth) return '';
    const [y, m] = summaryMonth.split('-');
    const d = new Date(parseInt(y), parseInt(m) - 1, 1);
    return d.toLocaleString('en-NZ', { month: 'long', year: 'numeric' });
  })();
  const monthPaid = invoices.reduce((sum, inv) => {
    const payments = inv.payments || [];
    return sum + payments
      .filter(p => monthMatches(p.created_at, summaryMonth))
      .reduce((s, p) => s + (p.amount || 0), 0);
  }, 0);
  const monthOverdueInvoices = invoices.filter(inv =>
    monthMatches(inv.due_date, summaryMonth) &&
    ((inv.paid_amount || 0) < (inv.amount || 0) - 0.005) &&
    inv.due_date && new Date(inv.due_date) < new Date()
  );
  const monthOverdue = monthOverdueInvoices.reduce(
    (sum, i) => sum + Math.max(0, (i.amount || 0) - (i.paid_amount || 0)), 0
  );
  const monthDueTotal = invoices
    .filter(inv => monthMatches(inv.due_date, summaryMonth))
    .reduce((s, i) => s + (i.amount || 0), 0);

  // ---------- Customer Invoices (money owed TO us) ----------
  const handleCustomerEmail = (inv) => {
    const balance = Math.max(0, (inv.total || 0) - (inv.paid_amount || 0));
    const storeName = 'The Wellness Store';
    const subject = encodeURIComponent(`Invoice ${inv.invoice_no} — Payment Reminder ($${balance.toFixed(2)} outstanding)`);
    const body = encodeURIComponent(
`Hi ${inv.customer_name || 'there'},

This is a friendly reminder that invoice ${inv.invoice_no} dated ${inv.invoice_date || ''} is currently outstanding.

  Total:    $${(inv.total || 0).toFixed(2)}
  Paid:     $${(inv.paid_amount || 0).toFixed(2)}
  Balance:  $${balance.toFixed(2)}${inv.due_date ? `\n  Due:      ${inv.due_date}` : ''}

You can transfer payment to our account, or pay in-store next time you visit.

Thank you,
${storeName}`
    );
    const toParam = (inv.customer_email || '').trim() ? `to=${encodeURIComponent(inv.customer_email.trim())}&` : '';
    window.open(`https://mail.google.com/mail/?view=cm&${toParam}su=${subject}&body=${body}`, '_blank');
  };

  const markCustomerInvoicePaid = async (inv) => {
    try {
      const total = inv.total || 0;
      const isPaid = (inv.paid_amount || 0) >= total - 0.005;
      const newPaid = isPaid ? 0 : total;
      await axios.put(`${API_URL}/customer-invoices/${inv.id}`, { paid_amount: newPaid }, { headers: getAuthHeader() });
      toast.success(isPaid ? 'Marked unpaid' : 'Marked paid in full');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    }
  };

  const deleteCustomerInvoice = async (inv) => {
    if (!window.confirm(`Delete customer invoice ${inv.invoice_no} for ${inv.customer_name} ($${(inv.total || 0).toFixed(2)})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/customer-invoices/${inv.id}`, { headers: getAuthHeader() });
      toast.success('Customer invoice deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  // Filter + derive customer-invoice rollups
  const filteredCustomerInvoices = (customerInvoices || []).filter(inv => {
    const total = inv.total || 0;
    const paid = inv.paid_amount || 0;
    const isFullyPaid = paid >= total - 0.005 && total > 0;
    if (custFilter === 'paid' && !isFullyPaid) return false;
    if (custFilter === 'unpaid' && isFullyPaid) return false;
    if (!custSearch.trim()) return true;
    const q = custSearch.toLowerCase();
    return (
      (inv.customer_name || '').toLowerCase().includes(q) ||
      (inv.customer_email || '').toLowerCase().includes(q) ||
      (inv.invoice_no || '').toLowerCase().includes(q)
    );
  }).sort((a, b) => {
    // unpaid first, then by due_date asc
    const aBal = Math.max(0, (a.total || 0) - (a.paid_amount || 0));
    const bBal = Math.max(0, (b.total || 0) - (b.paid_amount || 0));
    if ((aBal > 0) !== (bBal > 0)) return aBal > 0 ? -1 : 1;
    return (a.due_date || '').localeCompare(b.due_date || '');
  });

  const custTotalOutstanding = (customerInvoices || []).reduce((s, i) => s + Math.max(0, (i.total || 0) - (i.paid_amount || 0)), 0);
  const custOverdueCount = (customerInvoices || []).filter(i => (i.paid_amount || 0) < (i.total || 0) - 0.005 && i.due_date && new Date(i.due_date) < new Date()).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900" data-testid="invoices-h1">
            Invoices — {storeName(selectedStore)}
          </h1>
          <p className="text-slate-500 mt-1">
            Track who you owe and who owes you · showing <span className="font-semibold">{storeName(selectedStore)}</span> only
            <span className="text-xs text-slate-400 ml-1">(switch store from the sidebar)</span>
          </p>
        </div>
        {activeTab === 'suppliers' && (
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={resetForm} data-testid="add-invoice-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Invoice
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold uppercase">Add Invoice</DialogTitle>
              <DialogDescription>Record a supplier invoice</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label>Supplier *</Label>
                <Select value={invoiceForm.supplier_id} onValueChange={handleSupplierChange}>
                  <SelectTrigger data-testid="supplier-select">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Store *</Label>
                <Select value={invoiceForm.store} onValueChange={(v) => setInvoiceForm({ ...invoiceForm, store: v })}>
                  <SelectTrigger data-testid="store-select">
                    <SelectValue placeholder="Select store" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="orewa">{storeName('orewa')}</SelectItem>
                    <SelectItem value="eastridge">{storeName('eastridge')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Received Date</Label>
                  <Input
                    type="date"
                    value={invoiceForm.received_date}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, received_date: e.target.value })}
                    data-testid="received-date"
                  />
                </div>
                <div>
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={invoiceForm.due_date}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                    data-testid="due-date"
                  />
                </div>
              </div>
              
              <div>
                <Label>Invoice Number</Label>
                <Input
                  value={invoiceForm.invoice_number}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })}
                  placeholder="e.g. INV-2026-001"
                  data-testid="invoice-number-input"
                />
                <p className="text-xs text-slate-500 mt-1">Optional — supplier's invoice reference for your records.</p>
              </div>
              
              <div>
                <Label>Total Amount *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={invoiceForm.amount}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: parseFloat(e.target.value) || 0 })}
                  placeholder="Enter total invoice amount"
                  data-testid="amount-input"
                />
                <p className="text-xs text-slate-500 mt-1">Enter the full invoice total (GST inclusive)</p>
              </div>
              
              <div>
                <Label>Status</Label>
                <Select value={invoiceForm.status} onValueChange={(v) => setInvoiceForm({ ...invoiceForm, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={formLoading} className="bg-slate-900 hover:bg-slate-800">
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Invoice'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Tabs: Suppliers (we owe) vs Customers (owed to us) */}
      <div className="flex gap-2 border-b border-slate-200" data-testid="invoices-tabs">
        <button
          type="button"
          onClick={() => setActiveTab('suppliers')}
          className={`px-4 py-2 text-sm font-bold uppercase tracking-wide -mb-px border-b-2 transition-colors ${activeTab === 'suppliers' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-suppliers"
        >
          <FileText className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Suppliers — We Owe
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('customers')}
          className={`px-4 py-2 text-sm font-bold uppercase tracking-wide -mb-px border-b-2 transition-colors ${activeTab === 'customers' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-customers"
        >
          <Users className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Customers — Owed To Us
          {custOverdueCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 border border-red-200">{custOverdueCount} overdue</span>
          )}
        </button>
      </div>

      {activeTab === 'suppliers' && (
      <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">Unpaid</p>
                <p className="text-2xl font-bold text-red-700">${totalUnpaid.toFixed(2)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-600">Paid</p>
                <p className="text-2xl font-bold text-emerald-700">${totalPaid.toFixed(2)}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={`border-2 ${overdueCount > 0 ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600">Overdue {overdueCount > 0 && <span className="text-xs">({overdueCount})</span>}</p>
                <p className="text-2xl font-bold text-amber-700">${overdueAmount.toFixed(2)}</p>
              </div>
              <AlertTriangle className={`w-8 h-8 ${overdueCount > 0 ? 'text-amber-400' : 'text-slate-300'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly summary card — pick a month, see Paid + Overdue + Total Due for that month */}
      <Card className="border-2 border-indigo-200 bg-indigo-50/50" data-testid="monthly-summary-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[220px]">
              <p className="text-xs uppercase font-semibold text-indigo-700 mb-1">Monthly summary</p>
              <div className="flex items-center gap-3">
                <Input
                  type="month"
                  value={summaryMonth}
                  onChange={(e) => setSummaryMonth(e.target.value)}
                  className="w-44"
                  data-testid="summary-month-picker"
                />
                <span className="text-sm font-bold text-indigo-900">{monthLabel}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="bg-white rounded p-3 min-w-[160px] border border-emerald-200">
                <p className="text-xs uppercase text-emerald-700 font-semibold">Paid in {monthLabel}</p>
                <p className="text-xl font-bold text-emerald-700">${monthPaid.toFixed(2)}</p>
              </div>
              <div className="bg-white rounded p-3 min-w-[160px] border border-amber-200">
                <p className="text-xs uppercase text-amber-700 font-semibold">Overdue in {monthLabel}</p>
                <p className="text-xl font-bold text-amber-700">${monthOverdue.toFixed(2)}</p>
                {monthOverdueInvoices.length > 0 && (
                  <p className="text-[11px] text-amber-600 mt-0.5">{monthOverdueInvoices.length} invoice(s)</p>
                )}
              </div>
              <div className="bg-white rounded p-3 min-w-[160px] border border-slate-200">
                <p className="text-xs uppercase text-slate-600 font-semibold">Total due in {monthLabel}</p>
                <p className="text-xl font-bold text-slate-800">${monthDueTotal.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search supplier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Invoices Table */}
      <Card className="border rounded-sm">
        <CardHeader className="border-b bg-slate-50">
          <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
            <FileText className="w-5 h-5" /> Invoices ({filteredInvoices.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-heading font-bold uppercase text-xs">Supplier</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs">Store</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs">Received</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs">Due Date</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs text-right">Total</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs text-right">Paid</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs text-right">Balance</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs text-center">Status</TableHead>
                <TableHead className="w-44"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    No invoices found
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvoices.map((invoice) => {
                  const total = invoice.amount || 0;
                  const paid = invoice.paid_amount || 0;
                  const balance = Math.max(0, total - paid);
                  const isFullyPaid = paid >= total - 0.005 && total > 0;
                  const isPartial = paid > 0 && !isFullyPaid;
                  const isOverdue = !isFullyPaid && invoice.due_date && new Date(invoice.due_date) < new Date();
                  const payments = invoice.payments || [];
                  const isExpanded = expanded[invoice.id];
                  return (
                    <React.Fragment key={invoice.id}>
                    <TableRow className={isOverdue ? 'bg-red-50' : ''} data-testid={`invoice-row-${invoice.id}`}>
                      <TableCell className="font-medium">
                        <button
                          type="button"
                          onClick={() => toggleExpand(invoice.id)}
                          className="inline-flex items-center gap-1 text-left hover:text-blue-600"
                          disabled={payments.length === 0}
                          data-testid={`expand-${invoice.id}`}
                        >
                          {payments.length > 0 && (
                            isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />
                          )}
                          {invoice.supplier_name}
                          {payments.length > 0 && <span className="text-[10px] text-blue-500 font-normal">({payments.length})</span>}
                        </button>
                      </TableCell>
                      <TableCell className="text-slate-600 font-mono text-xs">
                        {invoice.invoice_number || <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={invoice.store === 'eastridge' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'}>
                          {storeName(invoice.store)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-slate-600">
                          <Calendar className="w-3 h-3" />
                          {invoice.received_date ? formatDate(invoice.received_date) : '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                          <Clock className="w-3 h-3" />
                          {invoice.due_date ? formatDate(invoice.due_date) : '-'}
                          {isOverdue && <AlertTriangle className="w-3 h-3 ml-1" />}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold">${total.toFixed(2)}</TableCell>
                      <TableCell className="text-right text-emerald-700">${paid.toFixed(2)}</TableCell>
                      <TableCell className={`text-right font-bold ${balance > 0 ? 'text-red-600' : 'text-slate-400'}`}>${balance.toFixed(2)}</TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          className={isFullyPaid 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : isPartial
                              ? 'bg-amber-100 text-amber-800'
                              : (!isOverdue && invoice.due_date)
                                ? 'bg-slate-100 text-slate-600'
                                : 'bg-red-100 text-red-700'
                          }
                        >
                          {isFullyPaid ? 'Paid' : isPartial ? 'Partial' : (!isOverdue && invoice.due_date) ? 'Not due yet' : 'Unpaid'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 justify-end">
                          {!isFullyPaid && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPayDialog(invoice)}
                              className="border-blue-200 text-blue-600 hover:bg-blue-50"
                              data-testid={`record-payment-${invoice.id}`}
                            >
                              <CreditCard className="w-3 h-3 mr-1" /> Pay
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => togglePaidStatus(invoice)}
                            className={isFullyPaid 
                              ? 'border-red-200 text-red-600 hover:bg-red-50'
                              : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'
                            }
                            data-testid={`toggle-status-${invoice.id}`}
                          >
                            {isFullyPaid ? 'Unpaid' : 'Paid in full'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteInvoice(invoice)}
                            className="text-slate-400 hover:text-red-600 hover:bg-red-50 px-2"
                            title="Delete invoice"
                            data-testid={`delete-invoice-${invoice.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {isExpanded && payments.length > 0 && (
                      <TableRow className="bg-slate-50/60">
                        <TableCell colSpan={10} className="p-0">
                          <div className="px-6 py-3">
                            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">Payment History</div>
                            <div className="space-y-1">
                              {payments.slice().sort((a,b) => (a.date < b.date ? 1 : -1)).map((p) => (
                                <div key={p.id} className="flex items-center justify-between gap-3 text-sm py-1.5 border-b last:border-b-0 border-slate-200">
                                  <div className="flex items-center gap-3">
                                    <span className="font-bold text-emerald-700">${(p.amount || 0).toFixed(2)}</span>
                                    <span className="text-slate-500">
                                      {p.date ? new Date(p.date).toLocaleDateString('en-NZ', { day:'2-digit', month:'short', year:'numeric' }) : ''}
                                    </span>
                                    {p.by && <span className="text-xs text-slate-400">— {p.by}</span>}
                                    {p.note && <span className="text-xs italic text-slate-500">"{p.note}"</span>}
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deletePayment(invoice.id, p.id)}
                                    className="h-6 px-2 text-xs text-slate-400 hover:text-red-600"
                                    data-testid={`delete-payment-${p.id}`}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })
              )}
              {filteredInvoices.length > 0 && (
                <TableRow className="bg-slate-100 font-bold border-t-2 border-slate-300">
                  <TableCell colSpan={5} className="text-right text-xs uppercase tracking-wider text-slate-700">Totals ({filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''})</TableCell>
                  <TableCell className="text-right text-base">${filteredInvoices.reduce((s, i) => s + (i.amount || 0), 0).toFixed(2)}</TableCell>
                  <TableCell className="text-right text-base text-emerald-700">${filteredInvoices.reduce((s, i) => s + (i.paid_amount || 0), 0).toFixed(2)}</TableCell>
                  <TableCell className="text-right text-base text-red-600">${filteredInvoices.reduce((s, i) => s + Math.max(0, (i.amount || 0) - (i.paid_amount || 0)), 0).toFixed(2)}</TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </>
      )}

      {activeTab === 'customers' && (
      <>
        {/* Customer Invoices summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">Outstanding (owed to us)</p>
                <p className="text-2xl font-bold text-red-700">${custTotalOutstanding.toFixed(2)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-red-400" />
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600">Overdue customers</p>
                <p className="text-2xl font-bold text-amber-700">{custOverdueCount}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-400" />
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total customer invoices</p>
                <p className="text-2xl font-bold text-slate-800">{customerInvoices.length}</p>
              </div>
              <Users className="w-8 h-8 text-slate-400" />
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-4 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search customer, email, invoice #..."
              value={custSearch}
              onChange={(e) => setCustSearch(e.target.value)}
              className="pl-10"
              data-testid="cust-search"
            />
          </div>
          <Select value={custFilter} onValueChange={setCustFilter}>
            <SelectTrigger className="w-[170px]" data-testid="cust-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unpaid">Unpaid / Partial</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto text-xs text-slate-500 self-center max-w-md">
            Tip: Click <Mail className="w-3 h-3 inline mb-0.5" /> next to a customer to open Gmail with a pre-written payment reminder — just like sending a PO.
          </div>
        </div>

        {/* Customer Invoices table */}
        <Card className="border rounded-sm">
          <CardHeader className="border-b bg-slate-50">
            <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2 flex-wrap">
              <Users className="w-5 h-5" /> Customers Who Need To Pay ({filteredCustomerInvoices.length})
              {selectedStore && (
                <Badge className={selectedStore === 'orewa' ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white'} data-testid="cust-invoices-store-badge">
                  {`${storeName(selectedStore)} only`}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-heading font-bold uppercase text-xs">Customer</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs">Invoice #</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs">Date</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs">Due</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs text-right">Total</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs text-right">Paid</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs text-right">Balance</TableHead>
                  <TableHead className="font-heading font-bold uppercase text-xs text-center">Status</TableHead>
                  <TableHead className="w-44"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomerInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      No customer invoices match this filter
                      <p className="text-xs mt-1">Create one from Smart PO → "Customer Invoice" button.</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomerInvoices.map((inv) => {
                    const total = inv.total || 0;
                    const paid = inv.paid_amount || 0;
                    const balance = Math.max(0, total - paid);
                    const isFullyPaid = paid >= total - 0.005 && total > 0;
                    const isPartial = paid > 0 && !isFullyPaid;
                    const isOverdue = !isFullyPaid && inv.due_date && new Date(inv.due_date) < new Date();
                    return (
                      <TableRow key={inv.id} className={isOverdue ? 'bg-red-50' : ''} data-testid={`cust-invoice-row-${inv.id}`}>
                        <TableCell>
                          <div className="font-medium">{inv.customer_name || '—'}</div>
                          {inv.customer_email && (
                            <button
                              type="button"
                              onClick={() => handleCustomerEmail(inv)}
                              className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                              data-testid={`cust-email-link-${inv.id}`}
                              title="Open Gmail with a payment reminder draft"
                            >
                              <Mail className="w-3 h-3" /> {inv.customer_email}
                            </button>
                          )}
                          {!inv.customer_email && inv.customer_phone && (
                            <div className="text-xs text-slate-500">{inv.customer_phone}</div>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{inv.invoice_no}</TableCell>
                        <TableCell className="text-slate-600 text-sm">
                          {inv.invoice_date ? formatDate(inv.invoice_date) : '-'}
                        </TableCell>
                        <TableCell>
                          <div className={`flex items-center gap-1 text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                            <Clock className="w-3 h-3" />
                            {inv.due_date ? formatDate(inv.due_date) : '-'}
                            {isOverdue && <AlertTriangle className="w-3 h-3 ml-1" />}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-bold">${total.toFixed(2)}</TableCell>
                        <TableCell className="text-right text-emerald-700">${paid.toFixed(2)}</TableCell>
                        <TableCell className={`text-right font-bold ${balance > 0 ? 'text-red-600' : 'text-slate-400'}`}>${balance.toFixed(2)}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={isFullyPaid ? 'bg-emerald-100 text-emerald-700' : isPartial ? 'bg-amber-100 text-amber-800' : (!isOverdue && inv.due_date) ? 'bg-slate-100 text-slate-600' : 'bg-red-100 text-red-700'}>
                            {isFullyPaid ? 'Paid' : isPartial ? 'Partial' : (!isOverdue && inv.due_date) ? 'Not due yet' : 'Unpaid'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCustomerEmail(inv)}
                              className="border-blue-200 text-blue-600 hover:bg-blue-50"
                              title="Open Gmail draft"
                              data-testid={`cust-email-btn-${inv.id}`}
                            >
                              <Mail className="w-3 h-3 mr-1" /> Email
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => markCustomerInvoicePaid(inv)}
                              className={isFullyPaid
                                ? 'border-red-200 text-red-600 hover:bg-red-50'
                                : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'
                              }
                              data-testid={`cust-toggle-paid-${inv.id}`}
                            >
                              {isFullyPaid ? 'Unpaid' : 'Paid'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteCustomerInvoice(inv)}
                              className="text-slate-400 hover:text-red-600 hover:bg-red-50 px-2"
                              title="Delete invoice"
                              data-testid={`cust-delete-${inv.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
                {filteredCustomerInvoices.length > 0 && (
                  <TableRow className="bg-slate-100 font-bold border-t-2 border-slate-300">
                    <TableCell colSpan={4} className="text-right text-xs uppercase tracking-wider text-slate-700">Totals ({filteredCustomerInvoices.length})</TableCell>
                    <TableCell className="text-right text-base">${filteredCustomerInvoices.reduce((s, i) => s + (i.total || 0), 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-base text-emerald-700">${filteredCustomerInvoices.reduce((s, i) => s + (i.paid_amount || 0), 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-base text-red-600">${filteredCustomerInvoices.reduce((s, i) => s + Math.max(0, (i.total || 0) - (i.paid_amount || 0)), 0).toFixed(2)}</TableCell>
                    <TableCell colSpan={2}></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </>
      )}

      {/* Record Payment Dialog */}
      <Dialog open={payDialogOpen} onOpenChange={(open) => { setPayDialogOpen(open); if (!open) { setPayInvoice(null); setPayAmount(''); setPayNote(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Record Payment</DialogTitle>
            <DialogDescription>
              {payInvoice ? `${payInvoice.supplier_name} — Invoice total $${(payInvoice.amount || 0).toFixed(2)}` : ''}
            </DialogDescription>
          </DialogHeader>
          {payInvoice && (
            <form onSubmit={handleRecordPayment} className="space-y-4">
              <div className="rounded-md border border-slate-200 p-3 text-sm space-y-1 bg-slate-50">
                <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-medium">${(payInvoice.amount || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Already paid</span><span className="font-medium text-emerald-700">${(payInvoice.paid_amount || 0).toFixed(2)}</span></div>
                <div className="flex justify-between border-t border-slate-200 pt-1 mt-1">
                  <span className="text-slate-600 font-medium">Outstanding</span>
                  <span className="font-bold text-red-600">${Math.max(0, (payInvoice.amount || 0) - (payInvoice.paid_amount || 0)).toFixed(2)}</span>
                </div>
              </div>
              <div>
                <Label>Amount paying now</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  placeholder="0.00"
                  autoFocus
                  data-testid="pay-amount-input"
                />
                <p className="text-[11px] text-slate-400 mt-1">Pre-filled with the outstanding balance — overwrite if paying a partial amount.</p>
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Input
                  value={payNote}
                  onChange={(e) => setPayNote(e.target.value)}
                  placeholder="e.g. paid via internet banking, cheque #1234"
                  data-testid="pay-note-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setPayDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={paySaving} className="bg-blue-600 hover:bg-blue-700" data-testid="pay-submit">
                  {paySaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-2" /> Record Payment</>}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierInvoices;
